import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../helpers/axiosInstance";
import PincodeRanges from "./PincodeRanges";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman & Nicobar",
  "Chandigarh",
  "Dadra & Nagar Haveli",
  "Delhi",
  "Jammu & Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
  "Unknown"
];

const RateCardManagement = () => {
  const [rateCard, setRateCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pincodeModal, setPincodeModal] = useState(false);
  const [freeDeliveryModal, setFreeDeliveryModal] = useState(false);
  const [pincodeData, setPincodeData] = useState({
    state: "",
    price: "",
  });
  const [freeDeliveryPincode, setFreeDeliveryPincode] = useState("");

  const breadcrumbItems = [
    { title: "Toy Hub Admin", link: "/" },
    { title: "Shipping Rate Card", link: "#" },
  ];

  useEffect(() => {
    fetchRateCard();
  }, []);

  const fetchRateCard = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/v1/admin/ratecard");
      setRateCard(response.data.rateCard);
    } catch (error) {
      console.error("Error fetching rate card:", error);
      toast.error("Failed to fetch rate card settings");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPincodeRange = async (e) => {
    e.preventDefault();
    if (!pincodeData.state || !pincodeData.price) {
      toast.error("Please select a state and enter price");
      return;
    }
    try {
      await axiosInstance.post("/api/v1/admin/ratecard/pincode", pincodeData);
      toast.success("State pincodes added successfully");
      setPincodeModal(false);
      fetchRateCard();
      resetPincodeForm();
    } catch (error) {
      console.error("Error adding state pincodes:", error);
      toast.error("Failed to add state pincodes");
    }
  };

  const handleRemovePincodeRange = async (rangeId) => {
    if (window.confirm("Are you sure you want to remove this pincode range?")) {
      try {
        await axiosInstance.delete(`/api/v1/admin/ratecard/pincode/${rangeId}`);
        toast.success("Pincode range removed successfully");
        fetchRateCard();
      } catch (error) {
        console.error("Error removing pincode range:", error);
        toast.error("Failed to remove pincode range");
      }
    }
  };

  const handleAddFreeDeliveryPincode = async (e) => {
    e.preventDefault();
    if (!freeDeliveryPincode) {
      toast.error("Please enter a pincode");
      return;
    }
    try {
      await axiosInstance.put("/api/v1/admin/ratecard", {
        freeDeliveryPincodes: freeDeliveryPincode
      });
      toast.success("Free delivery pincode added successfully");
      setFreeDeliveryModal(false);
      setFreeDeliveryPincode("");
      fetchRateCard();
    } catch (error) {
      console.error("Error adding free delivery pincode:", error);
      toast.error("Failed to add free delivery pincode");
    }
  };



  const resetPincodeForm = () => {
    setPincodeData({
      state: "",
      price: "",
    });
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Shipping Rate Card"
            breadcrumbItems={breadcrumbItems}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h4 className="card-title mb-0">Shipping Rate Card</h4>
                    </div>
                    <div>
                      <Button
                        color="info"
                        className="me-2"
                        onClick={() => {
                          setFreeDeliveryPincode(rateCard?.freeDeliveryPincodes);
                          setFreeDeliveryModal(true);
                        }}
                      >
                        {rateCard?.freeDeliveryPincodes ? "Update Free Delivery Pincode" : "Add Free Delivery Pincode"}
                      </Button>
                      <Button
                        color="success"
                        onClick={() => setPincodeModal(true)}
                      >
                        Add State Pincodes
                      </Button>
                    </div>
                  </div>

                  {rateCard && (
                    <>
                      <div className="mb-4">
                        <h5>Free Delivery Pincodes</h5>
                        {rateCard?.freeDeliveryPincodes ? (
                          <div className="d-flex flex-wrap gap-2">
                              <div  className="badge bg-success p-2">
                                {rateCard?.freeDeliveryPincodes}
                              </div>
                          </div>
                        ) : (
                          <p>No free delivery pincodes added</p>
                        )}
                      </div>
                      <PincodeRanges
                        ranges={rateCard.pincodeRanges}
                        warehousePincode={rateCard.warehousePincode}
                        onRemoveRange={handleRemovePincodeRange}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={pincodeModal}
            toggle={() => setPincodeModal(!pincodeModal)}
          >
            <ModalHeader toggle={() => setPincodeModal(!pincodeModal)}>
              Add State Pincodes
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleAddPincodeRange}>
                <FormGroup>
                  <Label for="state">Select State</Label>
                  <Input
                    type="select"
                    name="state"
                    id="state"
                    value={pincodeData.state}
                    onChange={(e) =>
                      setPincodeData({ ...pincodeData, state: e.target.value })
                    }
                    required
                  >
                    <option value="">Choose state...</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="price">Shipping Price (₹)</Label>
                  <Input
                    type="number"
                    name="price"
                    id="price"
                    placeholder="Enter shipping price"
                    value={pincodeData.price}
                    onChange={(e) =>
                      setPincodeData({ ...pincodeData, price: e.target.value })
                    }
                    required
                  />
                </FormGroup>
                <Button color="primary" type="submit">
                  Add State Pincodes
                </Button>
              </Form>
            </ModalBody>
          </Modal>

          <Modal
            isOpen={freeDeliveryModal}
            toggle={() => setFreeDeliveryModal(!freeDeliveryModal)}
          >
            <ModalHeader toggle={() => setFreeDeliveryModal(!freeDeliveryModal)}>
              Add Free Delivery Pincode
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleAddFreeDeliveryPincode}>
                <FormGroup>
                  <Label for="freeDeliveryPincode">Pincode</Label>
                  <Input
                    type="number"
                    name="freeDeliveryPincode"
                    id="freeDeliveryPincode"
                    placeholder="Enter pincode"
                    value={freeDeliveryPincode}
                    onChange={(e) => setFreeDeliveryPincode(e.target.value)}
                    required
                    min="100000"
                    max="999999"
                  />
                </FormGroup>
                <Button color="primary" type="submit">
                  Add Free Delivery Pincode
                </Button>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RateCardManagement;
